import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { FinalLeaderboardEntry } from "@binogi-livequiz/shared";
import { Podium } from "../components/Podium";
import { Leaderboard } from "../components/Leaderboard";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { Button } from "../components/ui/Button";
import { useWebSocket } from "../lib/ws-context";
import { serverErrorKey } from "../lib/i18n";
import { HostErrorBanner } from "../components/HostErrorBanner";

interface RouteState {
  finalLeaderboard: FinalLeaderboardEntry[];
  availableLanguages?: string[];
}

export default function HostResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state as RouteState | null;
  const final = state?.finalLeaderboard ?? [];
  const ws = useWebSocket();
  // An i18n key, translated at render. Another screen attaching to this
  // session answers here with NOT_TEACHER, as on the lobby and question.
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const msg = ws.lastMessage;
    if (!msg) return;
    if (msg.type === "ERROR") {
      setError(
        serverErrorKey(msg, {
          overrides: { NOT_TEACHER: "host.errors.taken_over" },
        }),
      );
    }
    ws.consume();
  }, [ws]);

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-3xl mx-auto">
      <ScreenHeader
        available={state?.availableLanguages}
        trailing={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/host")}
            className="text-white/60 hover:text-white"
          >
            {t("host.results.new_session")}
          </Button>
        }
      />

      {error && <HostErrorBanner message={t(error)} onDismiss={() => setError(null)} />}

      <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">
        {t("host.results.heading")}
      </h1>

      {final.length === 0 ? (
        <p className="text-white/60 text-center py-12">
          {t("host.results.none")}
        </p>
      ) : (
        <>
          <div className="mb-10">
            <Podium entries={final} />
          </div>

          <h2 className="text-lg font-semibold mb-3">
            {t("host.results.all_players")}
          </h2>
          <Leaderboard entries={final} />
        </>
      )}

      <div className="mt-10 flex justify-center">
        <Button onClick={() => navigate("/host")} className="bg-brand-teal hover:brightness-110">
          {t("host.results.end_session")}
        </Button>
      </div>
    </main>
  );
}
